<template>
  <div>
    <div
      v-if="permissions && permissions.can_view_project && project"
      id="project-detail"
      class="project-home"
    >
      <div class="project-home__card">
        <ProjectsListItem
          standalone
          :project="project"
          @open-modal="onProjectCardAction"
        />
        <div v-if="arraysOffline.length > 0" class="project-home__offline">
          {{ arraysOffline.length }} modification<span
            v-if="arraysOffline.length > 1"
            >s</span
          >
          en attente
          <button
            type="button"
            class="ui compact teal button"
            :disabled="!isOnline"
            @click="sendOfflineFeatures"
          >
            Envoyer au serveur
          </button>
        </div>
      </div>

      <div class="project-home__body">
        <div class="project-home__left">
          <div class="project-home__actions">
            <router-link
              class="project-home__action"
              :to="{
                name: 'liste-signalements',
                params: { slug },
                query: { vue: 'liste' },
              }"
            >
              <img src="@/assets/img/project-detail/arrow-right.png" alt="" />
              Gestion des signalements
            </router-link>
            <router-link
              class="project-home__action"
              :to="{
                name: 'liste-signalements',
                params: { slug },
              }"
            >
              <img src="@/assets/img/project-detail/map.png" alt="" />
              Carte des signalements
            </router-link>
          </div>

          <div class="project-home__panel">
            <ProjectFeatureTypes
              :loading="featureTypesLoading"
              :project="project"
              @delete="toggleDeleteFeatureTypeModal"
              @update="updateAfterImport"
            />
          </div>

          <div class="project-home__recents">
            <ProjectLastFeatures ref="lastFeatures" />
            <span
              class="project-home__recents-divider"
              aria-hidden="true"
            />
            <ProjectLastComments :loading="projectInfoLoading" />
          </div>
        </div>

        <div class="project-home__map">
          <div class="map-container">
            <div id="map" ref="map" />
            <div :class="{ active: mapLoading }" class="ui inverted dimmer">
              <div class="ui text loader">Chargement de la carte...</div>
            </div>
            <MapTilesLoader />
            <SidebarLayers
              v-if="basemaps && map && !projectInfoLoading"
              ref="sidebar"
            />
            <Geolocation />
            <ItineraryButton />
            <div id="popup" class="ol-popup">
              <a id="popup-closer" href="#" class="ol-popup-closer" />
              <div id="popup-content" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <span v-else-if="!projectInfoLoading">
      <i class="icon exclamation triangle" aria-hidden="true" />
      <span
        >Vous ne disposez pas des droits nécessaires pour consulter ce
        projet.</span
      >
    </span>

    <ProjectModal
      :is-subscriber="is_suscriber"
      :feature-type-to-delete="featureTypeToDelete"
      @action="handleModalAction"
    />
  </div>
</template>

<script>
import mapService from "@/services/map-service";
import { mapGetters, mapState, mapActions, mapMutations } from "vuex";

import projectAPI from "@/services/project-api";
import featureTypeAPI from "@/services/featureType-api";
import featureAPI from "@/services/feature-api";

import ProjectsListItem from "@/components/Projects/ProjectsListItem";
import ProjectFeatureTypes from "@/components/Project/Detail/ProjectFeatureTypes";
import ProjectLastFeatures from "@/components/Project/Detail/ProjectLastFeatures";
import ProjectLastComments from "@/components/Project/Detail/ProjectLastComments";
import ProjectModal from "@/components/Project/Detail/ProjectModal";
import SidebarLayers from "@/components/Map/SidebarLayers";
import Geolocation from "@/components/Map/Geolocation";
import ItineraryButton from "@/components/Map/ItineraryButton";
import MapTilesLoader from "@/components/Map/MapTilesLoader";

export default {
  name: "ProjectDetail",

  components: {
    ProjectsListItem,
    ProjectFeatureTypes,
    ProjectLastFeatures,
    ProjectLastComments,
    ProjectModal,
    SidebarLayers,
    Geolocation,
    ItineraryButton,
    MapTilesLoader,
  },

  filters: {
    setDate(value) {
      const date = new Date(value);
      const d = date.toLocaleDateString("fr", {
        year: "2-digit",
        month: "numeric",
        day: "numeric",
      });
      return d;
    },
  },

  props: {
    message: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      infoMessage: "",
      importMessage: null,
      arraysOffline: [],
      arraysOfflineErrors: [],
      slug: this.$route.params.slug,
      is_suscriber: false,
      tempMessage: null,
      projectInfoLoading: true,
      featureTypesLoading: false,
      featureTypeToDelete: null,
      mapLoading: true,
    };
  },

  computed: {
    ...mapGetters(["permissions"]),
    ...mapState("projects", ["project"]),
    ...mapState(["configuration", "isOnline"]),
    ...mapState("feature", ["features"]),
    ...mapState("feature-type", ["feature_types"]),
    ...mapState(["user", "user_permissions", "reloadIntervalId"]),
    ...mapState("map", ["map", "basemaps", "availableLayers"]),
    API_BASE_URL() {
      return this.configuration.VUE_APP_DJANGO_API_BASE;
    },
    mvtFeatureLimit() {
      return Number(this.configuration.VUE_APP_MVT_FEATURE_LIMIT) || 1000;
    },
  },

  created() {
    if (this.user) {
      projectAPI
        .getProjectSubscription({
          baseUrl: this.$store.state.configuration.VUE_APP_DJANGO_API_BASE,
          projectSlug: this.$route.params.slug,
        })
        .then((data) => (this.is_suscriber = data.is_suscriber));
    }
  },

  mounted() {
    this.retrieveProjectInfo();
    if (this.message) {
      this.DISPLAY_MESSAGE(this.message);
    }
  },

  beforeDestroy() {
    this.$store.dispatch("CANCEL_CURRENT_SEARCH_REQUEST");
    this.CLEAR_RELOAD_INTERVAL_ID();
    this.CLOSE_PROJECT_MODAL();
  },

  methods: {
    ...mapMutations(["CLEAR_RELOAD_INTERVAL_ID", "DISPLAY_MESSAGE"]),
    ...mapMutations("modals", ["OPEN_PROJECT_MODAL", "CLOSE_PROJECT_MODAL"]),
    ...mapActions("projects", ["GET_PROJECT_INFO", "GET_PROJECT"]),
    ...mapActions("map", ["INITIATE_MAP"]),
    ...mapActions("feature", ["GET_PROJECT_FEATURES"]),
    ...mapActions("feature-type", ["GET_PROJECT_FEATURE_TYPES"]),

    retrieveProjectInfo() {
      Promise.all([
        this.GET_PROJECT(this.slug),
        this.GET_PROJECT_INFO(this.slug),
      ])
        .then(() => {
          this.$nextTick(() => {
            const map = mapService.getMap();
            if (map) mapService.destroyMap();
            this.initMap();
          });
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          this.projectInfoLoading = false;
        });
    },

    checkForOfflineFeature() {
      let arraysOffline = [];
      const localStorageArray = localStorage.getItem("geocontrib_offline");
      if (localStorageArray) {
        arraysOffline = JSON.parse(localStorageArray);
        this.arraysOffline = arraysOffline.filter(
          (x) => x.project === this.slug,
        );
      }
    },

    updateLocalStorage() {
      let arraysOffline = [];
      const localStorageArray = localStorage.getItem("geocontrib_offline");
      if (localStorageArray) {
        arraysOffline = JSON.parse(localStorageArray);
      }
      const arraysOfflineOtherProject = arraysOffline.filter(
        (x) => x.project !== this.slug,
      );
      this.arraysOffline = [];
      arraysOffline = arraysOfflineOtherProject.concat(
        this.arraysOfflineErrors,
      );
      localStorage.setItem("geocontrib_offline", JSON.stringify(arraysOffline));
    },

    subscribeProject() {
      projectAPI
        .subscribeProject({
          baseUrl: this.$store.state.configuration.VUE_APP_DJANGO_API_BASE,
          suscribe: !this.is_suscriber,
          projectSlug: this.$route.params.slug,
        })
        .then((data) => {
          this.is_suscriber = data.is_suscriber;
          this.CLOSE_PROJECT_MODAL();
          if (this.is_suscriber) {
            this.DISPLAY_MESSAGE({
              comment:
                "Vous êtes maintenant abonné aux notifications de ce projet.",
              level: "positive",
            });
          } else {
            this.DISPLAY_MESSAGE({
              comment: "Vous ne recevrez plus les notifications de ce projet.",
              level: "negative",
            });
          }
        });
    },

    deleteProject() {
      projectAPI
        .deleteProject(this.API_BASE_URL, this.slug)
        .then((response) => {
          if (response === "success") {
            this.$router.push("/");
            this.DISPLAY_MESSAGE({
              comment: `Le projet ${this.project.title} a bien été supprimé.`,
              level: "positive",
            });
          } else {
            this.DISPLAY_MESSAGE({
              comment: `Une erreur est survenu lors de la suppression du projet ${this.project.title}.`,
              level: "negative",
            });
          }
        });
    },

    deleteFeatureType() {
      featureTypeAPI
        .deleteFeatureType(this.featureTypeToDelete.slug)
        .then((response) => {
          this.CLOSE_PROJECT_MODAL();
          if (response === "success") {
            this.GET_PROJECT(this.slug);
            this.retrieveProjectInfo();
            this.DISPLAY_MESSAGE({
              comment: `Le type de signalement ${this.featureTypeToDelete.title} a bien été supprimé.`,
              level: "positive",
            });
          } else {
            this.DISPLAY_MESSAGE({
              comment: `Une erreur est survenu lors de la suppression du type de signalement ${this.featureTypeToDelete.title}.`,
              level: "negative",
            });
          }
          this.featureTypeToDelete = null;
        })
        .catch(() => {
          this.DISPLAY_MESSAGE({
            comment: `Une erreur est survenu lors de la suppression du type de signalement ${this.featureTypeToDelete.title}.`,
            level: "negative",
          });
          this.CLOSE_PROJECT_MODAL();
        });
    },

    handleModalAction(e) {
      switch (e) {
        case "subscribe":
          this.subscribeProject();
          break;
        case "deleteProject":
          this.deleteProject();
          break;
        case "deleteFeatureType":
          this.deleteFeatureType();
          break;
      }
    },

    onProjectCardAction({ type }) {
      if (type === "subscribe" || type === "deleteProject") {
        this.OPEN_PROJECT_MODAL(type);
      }
    },

    sendOfflineFeatures() {
      this.arraysOfflineErrors = [];
      const promises = this.arraysOffline.map((feature) =>
        featureAPI
          .postOrPutFeature({
            data: feature.geojson,
            feature_id: feature.featureId,
            project__slug: feature.project,
            feature_type__slug: feature.geojson.properties.feature_type,
            method: feature.type.toUpperCase(),
          })
          .then((response) => {
            if (!response) {
              this.arraysOfflineErrors.push(feature);
            }
          })
          .catch((error) => {
            console.error(error);
            this.arraysOfflineErrors.push(feature);
          }),
      );
      this.$store.commit("DISPLAY_LOADER", "Envoi des signalements en cours.");
      Promise.all(promises).then(() => {
        this.updateLocalStorage();
        this.retrieveProjectInfo();
        this.$store.commit("DISCARD_LOADER");
      });
    },

    toggleDeleteFeatureTypeModal(featureType) {
      this.featureTypeToDelete = featureType;
      this.OPEN_PROJECT_MODAL("deleteFeatureType");
    },
    /**
     * Initializes the map if the project is accessible and the user has view permissions.
     * This method sets up the map, loads vector tile layers, and handles offline features.
     */
    async initMap() {
      // Check if the project is accessible and the user has view permissions
      if (this.project && this.permissions.can_view_project) {
        // Initialize the map using the provided element reference
        await this.INITIATE_MAP({ el: this.$refs.map });
        // Check for offline features
        this.checkForOfflineFeature();
        // Define the URL for vector tile layers
        const mvtUrl = `${this.API_BASE_URL}features.mvt`;
        // Define parameters for loading layers
        const params = {
          project_slug: this.slug,
          featureTypes: this.feature_types,
          queryParams: {
            ordering: this.project.feature_browsing_default_sort,
            filter: this.project.feature_browsing_default_filter,
            ...(this.mvtFeatureLimit > 0
              ? { limit: this.mvtFeatureLimit }
              : {}),
          },
        };
        // Add vector tile layers to the map
        mapService.addVectorTileLayer({
          url: mvtUrl,
          ...params,
        });
        // Modify offline feature properties (setting color to 'red')
        this.arraysOffline.forEach((x) => (x.geojson.properties.color = "red"));
        // Extract offline features from arraysOffline
        const featuresOffline = this.arraysOffline.map((x) => x.geojson);
        // Add offline features to the map if available
        if (featuresOffline && featuresOffline.length > 0) {
          mapService.addFeatures({
            addToMap: true,
            features: featuresOffline,
            ...params,
          });
        }
        // Get the bounding box of features and fit the map to it
        featureAPI.getFeaturesBbox(this.slug).then((bbox) => {
          if (bbox) {
            mapService.fitBounds(bbox);
          }
          this.mapLoading = false; // Mark map loading as complete
        });
      }
    },

    updateAfterImport() {
      // reload feature types
      this.featureTypesLoading = true;
      this.GET_PROJECT_FEATURE_TYPES(this.slug).then(() => {
        this.featureTypesLoading = false;
      });
      // reload last features
      this.$refs.lastFeatures.fetchLastFeatures();
      // reload map
      const map = mapService.getMap();
      if (map) mapService.destroyMap();
      this.mapLoading = true;
      this.initMap();
    },
  },
};
</script>

<style lang="less" scoped>
.project-home {
  position: fixed;
  top: 55px;
  right: 0;
  bottom: 40px;
  left: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 0;
  padding: 20px 30px;
  background: #f3f5f8;
  overflow: hidden;
}

.project-home__card {
  flex: none;
  background: #fff;
  border-radius: 6px;
  padding: 0 1.15rem;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.08);
}

.project-home__offline {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0 0 0.85rem;
  color: #1b2142;
}

.project-home__body {
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
  gap: 20px;
}

.project-home__left {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: auto;
}

.project-home__actions {
  flex: none;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.project-home__action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-height: 2.6rem;
  padding: 0.45rem 0.8rem;
  border-radius: 6px;
  background: #3a468e;
  color: #fff;
  font-weight: 700;
  text-align: center;
  text-decoration: none;

  img {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    object-fit: contain;
  }

  &:hover {
    background: #2e3873;
    color: #fff;
  }
}

.project-home__panel {
  flex: none;
  background: #fff;
  border-radius: 6px;
  padding: 1rem;
  overflow: visible;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.08);
}

.project-home__recents {
  display: flex;
  justify-content: center;
  align-items: stretch;
  gap: 1.75rem;
  padding: 0.85rem 0.5rem 0.4rem;
}

.project-home__recents-divider {
  flex: none;
  align-self: stretch;
  width: 1px;
  margin: 0.15rem 0;
  background: #c8cdd6;
}

.project-home__map {
  min-width: 0;
  min-height: 0;
  display: flex;
  background: #fff;
  border-radius: 6px;
  padding: 0.55rem;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.12);
}

.map-container {
  position: relative;
  flex: 1;
  min-height: 0;
  border-radius: 4px;
  overflow: hidden;

  #map {
    position: absolute;
    inset: 0;
    border: 0;
  }
}

div.geolocation-container {
  top: calc(1.1em + 60px);
}

div.itinerary-container {
  top: calc(1.1em + 60px + 38px);
}

@media screen and (max-width: 900px) {
  .project-home__body,
  .project-home__actions {
    grid-template-columns: 1fr;
  }

  .project-home__recents {
    flex-direction: column;
    align-items: center;
  }

  .project-home__recents-divider {
    display: none;
  }

  .project-home__recents ::v-deep .project-recent {
    max-width: 100%;
  }

  .project-home {
    overflow: auto;
  }

  .project-home__map {
    min-height: 24rem;
  }
}

@media screen and (max-width: 725px) {
  .project-home {
    top: 116px;
  }
}
</style>
